import React, { useState } from 'react';

const TeamInfoStep = ({ data, onChange }) => {
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'sales_agent',
    position: '',
    preferredEmail: '',
  });

  // Calculate additional email cost
  const totalEmails = (data || []).length + 1; // +1 for the new member being added
  const additionalEmails = Math.max(0, totalEmails - 1); // First email is free
  const additionalCost = additionalEmails * 2500;

  const addTeamMember = () => {
    if (newMember.name && newMember.email && newMember.preferredEmail) {
      const updatedMembers = [...(data || []), { ...newMember, id: Date.now() }];
      onChange(updatedMembers);
      setNewMember({
        name: '',
        email: '',
        phone: '',
        role: 'sales_agent',
        position: '',
        preferredEmail: '',
      });
    }
  };

  const removeMember = (id) => {
    const updatedMembers = (data || []).filter(member => member.id !== id);
    onChange(updatedMembers);
  };

  // Calculate current cost based on existing members
  const currentEmails = (data || []).length;
  const currentAdditionalEmails = Math.max(0, currentEmails - 1);
  const currentAdditionalCost = currentAdditionalEmails * 2500;

  return (
    <div className="onboarding-step">
      <div className="step-header">
        <h2>Team Information</h2>
        <p>Add your team members who will be part of your franchise</p>
      </div>

      {/* Email Cost Summary */}
      <div style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '2px solid rgba(212, 175, 55, 0.3)',
        borderRadius: '16px',
        padding: '28px',
        marginBottom: '32px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
      }}>
        {/* Header Section */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '24px',
          paddingBottom: '20px',
          borderBottom: '2px solid rgba(212, 175, 55, 0.2)'
        }}>
          <div>
            <h3 style={{ 
              margin: '0 0 8px 0', 
              color: '#2c3e50', 
              fontSize: '22px',
              fontWeight: '600',
              letterSpacing: '-0.5px'
            }}>
              Email Account Pricing
            </h3>
            <p style={{ 
              margin: 0, 
              color: '#7f8c8d', 
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              Each team member receives a branded @douglasmccartney.com email
            </p>
          </div>
          <div style={{ 
            background: 'linear-gradient(135deg, var(--auth-gold) 0%, #e4c568 100%)',
            padding: '16px 24px',
            borderRadius: '12px',
            textAlign: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 16px rgba(212, 175, 55, 0.3)'
          }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: 'bold', 
              color: '#2c3e50',
              lineHeight: '1'
            }}>
              ₹{currentAdditionalCost.toLocaleString('en-IN')}
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: 'rgba(44, 62, 80, 0.7)',
              marginTop: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontWeight: '600'
            }}>
              Additional Cost
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '16px'
        }}>
          {/* Total Emails */}
          <div style={{ 
            background: '#ffffff',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px solid #e9ecef',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ 
              fontSize: '32px', 
              fontWeight: 'bold', 
              color: '#2c3e50',
              marginBottom: '8px',
              lineHeight: '1'
            }}>
              {currentEmails}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#7f8c8d',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontWeight: '500'
            }}>
              Total emails used
            </div>
          </div>

          {/* Free Email */}
          <div style={{ 
            background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px solid #28a745',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ 
              fontSize: '32px', 
              fontWeight: 'bold', 
              color: '#155724',
              marginBottom: '8px',
              lineHeight: '1'
            }}>
              1 Free
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#155724',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontWeight: '500'
            }}>
              Included
            </div>
          </div>

          {/* Additional Cost */}
          <div style={{ 
            background: 'linear-gradient(135deg, #fef5e7 0%, #fdebd0 100%)',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px solid var(--auth-gold)',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#9a7b3b',
              marginBottom: '8px',
              lineHeight: '1'
            }}>
              ₹2,500
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#9a7b3b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontWeight: '500'
            }}>
              Per Additional
            </div>
          </div>
        </div>
      </div>

      <div className="team-members-list">
        {data && data.length > 0 ? (
          <div className="members-grid">
            {data.map((member) => (
              <div key={member.id} className="member-card">
                <div className="member-info">
                  <div className="member-avatar">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="member-details">
                    <h4>{member.name}</h4>
                    <p className="member-email">📧 {member.email}</p>
                    <p className="member-email" style={{ color: 'var(--auth-gold)', fontWeight: '600' }}>
                      ✉️ {member.preferredEmail}@douglasmccartney.com
                    </p>
                    <p className="member-role">{member.position || member.role}</p>
                  </div>
                </div>
                <button
                  className="btn-remove-member"
                  onClick={() => removeMember(member.id)}
                  title="Remove member"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-members">
            <p>No team members added yet. Add your first team member below.</p>
          </div>
        )}
      </div>

      <div className="add-member-form">
        <h3>Add Team Member</h3>
        
        <div className="form-grid">
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={newMember.name}
              onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
              placeholder="John Doe"
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={newMember.email}
              onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
              placeholder="john@example.com"
            />
          </div>

          <div className="form-group">
            <label>Preferred Email Handle *</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="text"
                value={newMember.preferredEmail}
                onChange={(e) => setNewMember({ ...newMember, preferredEmail: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') })}
                placeholder="john.doe"
                style={{ flex: 1 }}
              />
              <span style={{ color: 'var(--auth-gold)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                @douglasmccartney.com
              </span>
            </div>
            <small style={{ color: '#888', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Only lowercase letters, numbers, dots, hyphens allowed
            </small>
          </div>

          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              value={newMember.phone}
              onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
              placeholder="+91 98765 43210"
            />
          </div>

          <div className="form-group">
            <label>Role *</label>
            <select
              value={newMember.role}
              onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
            >
              <option value="sales_agent">Sales Agent</option>
              <option value="manager">Manager</option>
              <option value="coordinator">Coordinator</option>
              <option value="marketing">Marketing Executive</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="form-group full-width">
            <label>Position/Title</label>
            <input
              type="text"
              value={newMember.position}
              onChange={(e) => setNewMember({ ...newMember, position: e.target.value })}
              placeholder="e.g., Senior Sales Executive"
            />
          </div>

          <div className="form-group full-width">
            <button
              type="button"
              className="btn-add-member"
              onClick={addTeamMember}
              disabled={!newMember.name || !newMember.email || !newMember.preferredEmail}
            >
              + Add Team Member
            </button>
          </div>
        </div>
      </div>

      <div className="step-note">
        <p><strong>Email Accounts:</strong> Your franchise includes 1 free @douglasmccartney.com email account. Additional email accounts for team members cost ₹2,500 each.</p>
        <p style={{ marginTop: '8px' }}><strong>Note:</strong> Email addresses will be provisioned within 24 hours of franchise approval.</p>
      </div>
    </div>
  );
};

export default TeamInfoStep;

